/**
 * ScrapeAnalyzeSection — Multi-URL scraping + AI analysis UI
 *
 * Replaces the single "Get Info from Link" button with:
 * - Multi-URL input (add/remove URLs)
 * - Extraction goals checkboxes
 * - Mode toggle (auto/review)
 * - Progress indicator during scraping
 * - Link to review panel when in review mode
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Stack, Box, Button, IconButton, Typography,
  Checkbox, FormControlLabel, FormGroup, Switch,
  LinearProgress, Chip, Alert, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import { StyledTextField } from '@modules/ui-components/forms/fields/StyledTextField';
import { getProviderForModel } from '@modules/ui-components/components/ModelSelector';
import {
  AvailableModelSelector,
  PROJECT_DEFAULT_AI_MODEL,
  useProjectDefaultAIModel,
} from '@modules/ai-settings';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { useBackendTask } from '@modules/content-agent/hooks/useBackendTask';
import { ScrapeAnalysisService } from '@modules/firebase-core/services/ScrapeAnalysisService';

export type ExtractionGoal = 'description' | 'tasks' | 'variables' | 'socialProofs';

interface ScrapeAnalyzeSectionProps {
  projectId: string;
  userId: string;
  /** Initial URL from the product link field */
  initialUrl?: string;
  /** Restrict available goals (e.g. ['socialProofs'] for Social Proofs tab) */
  allowedGoals?: ExtractionGoal[];
  /** Called when analysis completes (to refresh product data) */
  onComplete?: () => void;
  /** Called when review mode analysis completes (to show review panel) */
  onReviewReady?: (analysisId: string) => void;
  /** Called to open research history dialog */
  onOpenHistory?: () => void;
}

const GOAL_LABELS: Record<ExtractionGoal, string> = {
  description: 'Description',
  tasks: 'Tasks (JTBD)',
  variables: 'Variables',
  socialProofs: 'Social Proofs',
};

const PHASE_LABELS: Record<string, string> = {
  preparing: 'Preparing...',
  scraping: 'Scraping URLs...',
  analyzing_description: 'Analyzing: Description',
  analyzing_tasks: 'Analyzing: Tasks',
  analyzing_variables: 'Analyzing: Variables',
  analyzing_social_proofs: 'Analyzing: Social Proofs',
  saving: 'Saving results...',
  generating_report: 'Generating report...',
  completed: 'Completed!',
};

export function ScrapeAnalyzeSection({
  projectId,
  userId,
  initialUrl,
  allowedGoals,
  onComplete,
  onReviewReady,
  onOpenHistory,
}: ScrapeAnalyzeSectionProps) {
  const effectiveGoals = allowedGoals || (['description', 'tasks', 'variables', 'socialProofs'] as ExtractionGoal[]);
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const [urls, setUrls] = useState<string[]>(initialUrl ? [initialUrl] : ['']);
  const [goals, setGoals] = useState<ExtractionGoal[]>(effectiveGoals);
  const [mode, setMode] = useState<'auto' | 'review'>('review');
  const [model, setModel] = useState<string>(PROJECT_DEFAULT_AI_MODEL);
  const [modelTouched, setModelTouched] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load URLs from last research session on mount
  useEffect(() => {
    const service = new ScrapeAnalysisService(projectId);
    service.list({ limit: 1 }).then((analyses) => {
      if (analyses.length > 0 && analyses[0].urls?.length > 0) {
        setUrls(analyses[0].urls);
      }
    }).catch(() => {}); // Silent fail — just use defaults
  }, [projectId]);

  useEffect(() => {
    if (modelTouched) return;
    setModel(projectDefaultModel.value);
  }, [modelTouched, projectDefaultModel.value]);

  // Real-time task progress via Firestore listener
  const { task } = useBackendTask(projectId, taskId);

  const currentPhase = (task as any)?.phase as string | undefined;
  const phaseProgress = (task as any)?.phaseProgress as { currentPhase: number; totalPhases: number } | undefined;
  const isCompleted = task?.status === 'completed';
  const isFailed = task?.status === 'error';

  // Handle completion
  React.useEffect(() => {
    if (isCompleted && isRunning) {
      setIsRunning(false);
      if (mode === 'review' && analysisId) {
        onReviewReady?.(analysisId);
      } else {
        onComplete?.();
      }
    }
    if (isFailed && isRunning) {
      setIsRunning(false);
      setError((task as any)?.error || 'Analysis failed');
    }
  }, [isCompleted, isFailed, isRunning, mode, analysisId, onComplete, onReviewReady, task]);

  // URL management
  const addUrl = useCallback(() => setUrls(prev => [...prev, '']), []);
  const removeUrl = useCallback((index: number) => {
    setUrls(prev => prev.filter((_, i) => i !== index));
  }, []);
  const updateUrl = useCallback((index: number, value: string) => {
    setUrls(prev => prev.map((u, i) => i === index ? value : u));
  }, []);

  // Goal toggles
  const toggleGoal = useCallback((goal: ExtractionGoal) => {
    setGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  }, []);

  // Launch scraping + analysis
  const handleStart = useCallback(async () => {
    const validUrls = urls.filter(u => u.trim().length > 0);
    if (validUrls.length === 0) {
      setError('Add at least one URL');
      return;
    }
    if (goals.length === 0) {
      setError('Select at least one extraction goal');
      return;
    }

    setError(null);
    setIsRunning(true);

    try {
      const client = new AgentApiClient();
      const result = await client.scrapeAnalyze({
        projectId,
        userId,
        urls: validUrls,
        goals,
        mode,
        provider: getProviderForModel(model),
        model,
      });

      if (result.success && result.taskId) {
        setTaskId(result.taskId);
        setAnalysisId(result.analysisId || null);
      } else {
        setError(result.error || 'Failed to start analysis');
        setIsRunning(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start analysis');
      setIsRunning(false);
    }
  }, [goals, mode, model, projectId, urls, userId]);

  const progressPercent = phaseProgress
    ? (phaseProgress.currentPhase / phaseProgress.totalPhases) * 100
    : 0;

  return (
    <Stack spacing={2}>
      {/* Header: title + Add URL */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2">Product URLs</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addUrl}
          disabled={isRunning}
        >
          Add URL
        </Button>
      </Box>

      {urls.map((url, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <StyledTextField
            label={`URL ${index + 1}`}
            type="url"
            value={url}
            onChange={(value) => updateUrl(index, value)}
            fullWidth
            size="small"
            disabled={isRunning}
          />
          {urls.length > 1 && (
            <IconButton size="small" onClick={() => removeUrl(index)} disabled={isRunning}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}

      {/* Error */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Progress */}
      {isRunning && currentPhase && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {PHASE_LABELS[currentPhase] || currentPhase}
            </Typography>
            {phaseProgress && (
              <Typography variant="body2" color="text.secondary">
                {phaseProgress.currentPhase}/{phaseProgress.totalPhases}
              </Typography>
            )}
          </Box>
          <LinearProgress variant="determinate" value={progressPercent} />
        </Box>
      )}

      {/* Completed */}
      {isCompleted && (
        <Alert severity="success">
          Analysis completed!
          {mode === 'review' && analysisId && (
            <Button size="small" onClick={() => onReviewReady?.(analysisId)} sx={{ ml: 1 }}>
              Review Results
            </Button>
          )}
        </Alert>
      )}

      {/* ── Settings panel (opens above the button) ── */}
      <Collapse in={showSettings}>
        <Stack spacing={2} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {effectiveGoals.length > 1 && (
            <>
              <Typography variant="subtitle2">What to Extract</Typography>
              <FormGroup row>
                {(Object.entries(GOAL_LABELS) as Array<[ExtractionGoal, string]>)
                  .filter(([goal]) => effectiveGoals.includes(goal))
                  .map(([goal, label]) => (
                    <FormControlLabel
                      key={goal}
                      control={
                        <Checkbox
                          checked={goals.includes(goal)}
                          onChange={() => toggleGoal(goal)}
                          disabled={isRunning}
                          size="small"
                        />
                      }
                      label={label}
                    />
                  ))}
              </FormGroup>
            </>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'auto'}
                  onChange={(e) => setMode(e.target.checked ? 'auto' : 'review')}
                  disabled={isRunning}
                  size="small"
                />
              }
              label={mode === 'auto' ? 'Auto-apply enabled' : 'Auto-apply disabled — review before save'}
            />
            <Chip
              label={mode === 'auto' ? 'Auto' : 'Review'}
              size="small"
              color={mode === 'auto' ? 'success' : 'info'}
              variant="outlined"
            />
          </Box>
          <AvailableModelSelector
            projectId={projectId}
            value={model}
            onChange={(nextModel) => {
              setModelTouched(true);
              setModel(nextModel);
            }}
            label="AI Model"
            size="small"
            fullWidth
            disabled={isRunning}
            testId="scrape-analysis-model-selector"
          />
          {onOpenHistory && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={onOpenHistory}
              sx={{ alignSelf: 'flex-start' }}
            >
              Research History
            </Button>
          )}
        </Stack>
      </Collapse>

      {/* ── Scrape button + Settings icon ── */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<RocketLaunchIcon />}
          onClick={handleStart}
          disabled={isRunning || goals.length === 0 || urls.every(u => !u.trim())}
          sx={{
            px: 4,
            py: 1.2,
            fontSize: '1rem',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              animation: isRunning ? 'none' : 'shimmer 2.5s infinite',
            },
            '@keyframes shimmer': {
              '0%': { left: '-100%' },
              '100%': { left: '100%' },
            },
          }}
        >
          {isRunning ? 'Analyzing...' : 'Scrape & Analyze'}
        </Button>
        <IconButton
          size="small"
          onClick={() => setShowSettings(prev => !prev)}
          color={showSettings ? 'primary' : 'default'}
          title="Settings"
        >
          <TuneIcon />
        </IconButton>
      </Box>

    </Stack>
  );
}
